import React from "react";
import { useLocation } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb";

const SurveyDetails = () => {
  const location = useLocation();
  const survey = location.state?.survey;
  const fields = survey?.data || [];

  const groupedFields = fields.reduce((acc: any, field: any) => {
    const section = field.section || "Details";
    if (!acc[section]) acc[section] = [];
    acc[section].push(field);
    return acc;
  }, {});
  const formatDate = (value: string) => {
    if (!value) return "--";

    const parts = value.split(/[-/.]/).map(Number);
    if (parts.length !== 3) return "--";

    let [a, b, c] = parts;
    let day: number, month: number, year: number;

    if (a > 31) {
      // Format: YYYY-MM-DD
      year = a;
      month = b;
      day = c;
    } else if (c > 31) {
      // Format: DD-MM-YYYY or MM-DD-YYYY
      year = c;
      if (b > 12) {
        // Probably MM-DD-YYYY
        month = a;
        day = b;
      } else {
        // Probably DD-MM-YYYY
        day = a;
        month = b;
      }
    } else {
      // Ambiguous format - fallback to DD-MM-YYYY
      day = a;
      month = b;
      year = c;
    }

    const date = new Date(year, month - 1, day);
    return isNaN(date.getTime()) ? "--" : date.toLocaleDateString("en-GB");
  };

  const renderValue = (field: any) => {
    const { type, value, options } = field;
    switch (type) {
      case "date":
        return formatDate(value);

      case "file":
        const baseURL = process.env.REACT_APP_API_URL || "";
        return (
          <div className="flex gap-4 flex-wrap">
            {options?.length > 0 ? (
              options.map((file: any, idx: number) => {
                const lat = file.latitude;
                const lng = file.longitude;
                const imageUrl = file.value.startsWith("http")
                  ? file.value
                  : `${baseURL}/${file.value}`;
                const mapsLink =
                  lat && lng
                    ? `https://www.google.com/maps?q=${lat},${lng}`
                    : null;

                const handleImageClick = () => {
                  window.open(imageUrl, "_blank");
                };

                return (
                  <div key={idx} className="w-32 text-center space-y-1">
                    <img
                      src={imageUrl}
                      alt="Uploaded"
                      onClick={handleImageClick}
                      className="w-full h-24 object-cover rounded-lg border border-gray-300 dark:border-gray-600 shadow cursor-pointer hover:scale-105 transition-transform"
                    />
                    <div className="text-[12px] text-gray-700 dark:text-white">
                      <div>
                        <span className="font-semibold text-gray-500">
                          Lat:
                        </span>{" "}
                        {lat || "--"}
                      </div>
                      <div>
                        <span className="font-semibold text-gray-500">
                          Lng:
                        </span>{" "}
                        {lng || "--"}
                      </div>
                    </div>
                    {mapsLink && (
                      <a
                        href={mapsLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[12px] text-blue-600 dark:text-blue-400 underline"
                      >
                        View on Map
                      </a>
                    )}
                  </div>
                );
              })
            ) : (
              <span className="text-gray-500 dark:text-white">
                No file uploaded
              </span>
            )}
          </div>
        );
      case "zone":
        return (
          <div className="flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: field.color || "#000" }}
            ></span>
            <span>{value || "--"}</span>
          </div>
        );
      case "select":
      case "checkbox":
      case "radio":
        return options?.map((opt: any) => opt.value).join(", ") || "--";

      default:
        return value || "--";
    }
  };

  return (
    <>
      <Breadcrumb pageName="Survey Details" />

      <div className="rounded-sm border border-stroke bg-white px-5 pt-6 pb-4 shadow-md dark:border-strokedark dark:bg-boxdark sm:px-7.5 xl:pb-1 mt-8 max-w-7xl mx-auto">
        {Object.keys(groupedFields).map((section, sectionIndex) => (
          <div key={sectionIndex} className="mb-4">
            {/* Section Header */}
            <div className="text-lg font-semibold text-black dark:text-blue-300 mb-4 border-b border-gray-300 dark:border-gray-600 pb-2">
              {section}
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <tbody>
                  {groupedFields[section].map(
                    (field: any, fieldIndex: number) => (
                      <tr
                        key={fieldIndex}
                        className="border-b border-gray-200 dark:border-gray-700"
                      >
                        <td className="py-3 px-4 text-sm text-gray-800 dark:text-white font-medium">
                          {field.label}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700 dark:text-gray-300">
                          {renderValue(field)}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default SurveyDetails;
